import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { PROFILE_SERVICE_BASE_URL } from "constants";
import apiClient from "utils/apiClient";
import useDialMemberships from "./useDialMemberships";

const API_BASE_URL = `${PROFILE_SERVICE_BASE_URL}/api/v1/feature-dials`;

export default function useFeatureDials(page = 0, size = 10) {
  const authToken = sessionStorage.getItem("authToken");
  const [featureDials, setFeatureDials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ totalPages: 0, totalElements: 0 });
  const { getApplicationsForDial } = useDialMemberships();

  const handleAxiosErrors = (err, action = "operation") => {
    const message = err.response?.data?.message || err.message || `Failed to ${action}`;
    toast.error(message);
    console.error(`${action} error:`, err);
  };

  const withNoCache = (params = {}) => ({
    ...params,
    _ts: Date.now(),
  });

  const attachAppsToDials = useCallback(
    async (dials = []) => {
      if (!Array.isArray(dials) || dials.length === 0) return dials;
      try {
        const results = await Promise.all(
          dials.map(async (dial) => {
            const apps = await getApplicationsForDial(dial.id);
            return { id: dial.id, apps: Array.isArray(apps) ? apps : [] };
          })
        );
        const appMap = new Map(results.map((entry) => [String(entry.id), entry.apps]));
        return dials.map((dial) => ({
          ...dial,
          applications: appMap.get(String(dial.id)) || [],
        }));
      } catch (error) {
        return dials;
      }
    },
    [getApplicationsForDial]
  );

  const fetchAllFeatureDials = useCallback(
    async (customPage = page) => {
      if (!authToken) return;
      setLoading(true);
      try {
        const res = await apiClient.get(API_BASE_URL, {
          params: withNoCache({ page: customPage, size, sort: "createdAt,desc" }),
        });

        const data = res.data.content || res.data || [];
        const hydrated = await attachAppsToDials(data);
        setFeatureDials(hydrated);
        setPagination({
          totalPages: res.data.totalPages || 1,
          totalElements: res.data.totalElements || data.length,
        });
        return hydrated;
      } catch (error) {
        handleAxiosErrors(error, "fetch feature dials");
        return [];
      } finally {
        setLoading(false);
      }
    },
    [authToken, page, size]
  );

  const searchFeatureDials = useCallback(
    async (query = "", app = "ALL", searchPage = 0) => {
      if (!authToken) return;
      setLoading(true);
      try {
        const params = withNoCache({ page: searchPage, size, sort: "name,asc" });
        
        if (app !== "ALL") params.oliveebApp = app;
        if (query?.trim().length >= 2) params.q = query.trim();

        const isFiltered = Boolean(params.oliveebApp) || Boolean(params.q);
        const endpoint = isFiltered ? `${API_BASE_URL}/search` : API_BASE_URL;
        
        const res = await apiClient.get(endpoint, { params });
        const data = res.data.content || res.data || [];
        const hydrated = await attachAppsToDials(data);

        setFeatureDials(hydrated);
        setPagination({
          totalPages: res.data?.totalPages || 1,
          totalElements: res.data?.totalElements || data.length || 0,
        });
      } catch (error) {
        handleAxiosErrors(error, "search feature dials");
      } finally {
        setLoading(false);
      }
    },
    [authToken, size]
  );

  const addFeatureDial = useCallback(
    async (payload) => {
      if (!authToken) return;
      try {
        const res = await apiClient.post(API_BASE_URL, payload);
        toast.success("Feature dial created successfully");
        fetchAllFeatureDials(0);
        return res.data;
      } catch (error) {
        handleAxiosErrors(error, "create feature dial");
      }
    },
    [authToken, fetchAllFeatureDials]
  );

  const updateFeatureDial = useCallback(
    async (id, payload) => {
      if (!authToken) return;
      try {
        // Optimistic Update
        setFeatureDials((prev) =>
          prev.map((item) => (String(item.id) === String(id) ? { ...item, ...payload } : item))
        );

        const res = await apiClient.patch(`${API_BASE_URL}/${id}`, payload);
        
        // Re-sync with backend to ensure data integrity
        if (res.data) {
          setFeatureDials((prev) =>
            prev.map((item) => (String(item.id) === String(id) ? { ...item, ...res.data } : item))
          );
        }
        
        toast.success("Feature dial updated");
        return res.data;
      } catch (error) {
        // Rollback on error
        fetchAllFeatureDials();
        handleAxiosErrors(error, "update feature dial");
      }
    },
    [authToken, fetchAllFeatureDials]
  );

  const deleteFeatureDial = useCallback(
    async (id) => {
      if (!authToken) return;
      try {
        await apiClient.delete(`${API_BASE_URL}/${id}`);
        setFeatureDials((prev) => prev.filter((item) => item.id !== id));
        toast.success("Feature dial removed");
      } catch (error) {
        handleAxiosErrors(error, "delete feature dial");
      }
    },
    [authToken]
  );

  useEffect(() => {
    if (authToken) fetchAllFeatureDials(page);
  }, [authToken, fetchAllFeatureDials, page]);

  return {
    featureDials,
    loading,
    pagination,
    fetchAllFeatureDials,
    searchFeatureDials,
    addFeatureDial,
    updateFeatureDial,
    deleteFeatureDial,
  };
}
